import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Amenity = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublicModal, setShowPublicModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [formData, setFormData] = useState({ id: '', name: '', image: null });
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('amenityList');
      setAmenities(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch amenities');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({ id: '', name: '', image: null });
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (amenity) => {
    setFormData({ id: amenity._id, name: amenity.name, image: null });
    setImagePreview(amenity.image ? `${mediaUrl}${amenity.image}` : '');
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        toast.error('Please choose a smaller image less than 1MB');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      if (formData.id) data.append('id', formData.id);
      data.append('name', formData.name);
      if (formData.image) data.append('image', formData.image);

      await apiService.postFormData('updateAmenity', data);
      toast.success('Amenity updated successfully');
      setShowModal(false);
      fetchAmenities();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await apiService.get(`deleteAmenity/${selectedId}`);
      toast.success('Amenity deleted successfully');
      setShowDeleteModal(false);
      fetchAmenities();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakePublic = async () => {
    setSubmitting(true);
    try {
      await apiService.post('makePublic', { id: selectedId, type: 'amenity' });
      toast.success('Published successfully');
      setShowPublicModal(false);
      fetchAmenities();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const canEdit = (amenity) => {
    if (!amenity.vendorId) return true;
    return amenity.vendorId === user?._id || user?.role === 'admin';
  };

  return (
    <div className="flex-1 p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-b gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Amenity</h3>
          <button
            onClick={handleAddNew}
            className="flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#009ef7' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0084d4'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#009ef7'}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#009ef7'}}></div>
            </div>
          ) : amenities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No amenities found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {user?.role === 'admin' ? 'Public' : ''}
                    </th>
                    <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {amenities.map((amenity) => (
                    <tr key={amenity._id}>
                      <td className="px-2 sm:px-4 py-4">
                        <img
                          src={amenity.image ? `${mediaUrl}${amenity.image}` : '/assets/media/logos/checkmark.png'}
                          alt={amenity.name}
                          className="h-12 w-12 object-cover rounded"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">{amenity.name}</td>
                      <td className="px-2 sm:px-4 py-4">
                        {amenity.vendorId && user?.role === 'admin' && (
                          <button
                            onClick={() => {
                              setSelectedId(amenity._id);
                              setShowPublicModal(true);
                            }}
                            className="text-green-600 hover:text-green-700"
                            title="Make Public"
                          >
                            <i className="bi bi-check-circle text-2xl"></i>
                          </button>
                        )}
                      </td>
                      <td className="px-2 sm:px-4 py-4">
                        {canEdit(amenity) && (
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleEdit(amenity)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                            >
                              <i className="bi bi-pencil text-lg"></i>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedId(amenity._id);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <i className="bi bi-trash text-lg"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-end p-4 border-b">
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-center mb-6">Hotel Amenity</h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter Amenity Name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <div className="relative inline-block">
                    <div
                      className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden"
                      style={{
                        backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      {!imagePreview && <i className="bi bi-image text-3xl text-gray-400"></i>}
                    </div>
                    <label
                      className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center cursor-pointer hover:bg-gray-50"
                      title="Change icon"
                    >
                      <i className="bi bi-pencil text-xs"></i>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview('');
                          setFormData({ ...formData, image: null });
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center text-red-600 hover:bg-red-50"
                        title="Remove icon"
                      >
                        <i className="bi bi-x text-lg"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg"
                    style={{ backgroundColor: '#009ef7' }}
                  >
                    {submitting ? 'Please wait...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-gray-700">Are you sure you want to delete this Amenity?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                {submitting ? 'Please wait...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPublicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowPublicModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <p className="text-gray-700">Are you sure you want to make it public?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowPublicModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleMakePublic}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: '#009ef7' }}
              >
                {submitting ? 'Please wait...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Amenity;
